import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Table, Button as AntButton, DatePicker, Select, Checkbox } from 'antd';
import { MdArchive, MdPlayArrow } from 'react-icons/md';
import Button from 'components/Button/Button';
import { getSessionToken } from 'utils/session';
import { useNavigate } from 'react-router-dom';

const { RangePicker } = DatePicker;

const UpcomingRenewals = () => {
  const [userData, setUserData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [datePlaceHolder, setDatePlaceHolder] = useState({
    start_date: null,
    end_date: null,
  });
  const [products, setProducts] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [filters, setFilters] = useState({
    product: 0,
    dateRange: 0,
    showArchive: false,
  });
  const [selectedRows, setSelectedRows] = useState([]);

  const navigate = useNavigate();

  const handleStartJourney = (record) => {
    // navigate actions accordingly

    record.registration_no =
      record.transaction_id__transaction_user_input_motor_id__vehicle_registration_no;
    if (
      record.transaction_id__insurance_sub_product_id__alias == 'privatecar'
    ) {
      navigate('/posp/products/car-insurance', { state: { record } });
    } else if (record.transaction_id__insurance_sub_product_id__alias == '2w') {
      navigate('/posp/products/two-wheeler-insurance', { state: { record } });
    } else if (
      record.transaction_id__insurance_sub_product_id__alias == 'gcv'
    ) {
      navigate('/posp/products/gcv-insurance', { state: { record } });
    } else if (
      record.transaction_id__insurance_sub_product_id__alias == 'pcv'
    ) {
      navigate('/posp/products/pcv-insurance', { state: { record } });
    }
  };

  const fetchRenewals = async () => {
    setLoading(true);

    // Construct the params object dynamically
    const params = {};
    if (filters.product) {
      params.product_id = filters.product;
    }
    if (filters.dateRange?.[0]) {
      params.start_date = filters.dateRange[0];
    }
    if (filters.dateRange?.[1]) {
      params.end_date = filters.dateRange[1];
    }
    if (filters.showArchive) {
      params.is_archive = 1;
    }

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/pos/renewal/?page=${pagination.current}`,
        {
          headers: { Authorization: `Bearer ${getSessionToken()}` },
          params, // Pass dynamically constructed params
        }
      );
      setUserData(response.data.data || []);

      setDatePlaceHolder((prevFilters) => ({
        ...prevFilters,
        start_date: response.data.metadata.start_date
          .split('-')
          .reverse()
          .join('-'),
        end_date: response.data.metadata.end_date
          .split('-')
          .reverse()
          .join('-'),
      }));
      setTotal(response.data.metadata.total_pages || 0);
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRenewals();
  }, [pagination, filters]);

  // Fetch products from API
  const fetchProducts = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/ieadmin/product/`,
        {
          headers: { Authorization: `Bearer ${getSessionToken()}` },
        }
      );
      setProducts(response.data || []); // Update products state
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  useEffect(() => {
    fetchProducts(); // Load products when the component mounts
  }, []);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleRowSelect = (id, checked) => {
    setSelectedRows((prev) =>
      checked ? [...prev, id] : prev.filter((rowId) => rowId !== id)
    );
  };
  const handleSelectAll = (isChecked) => {
    if (isChecked) {
      setSelectedRows(userData.map((row) => row.transaction_id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleDoArchive = async () => {
    try {
      // Convert the selectedRows array into a comma-separated string
      const id_list = selectedRows.join(',');

      // Make the POST request with the comma-separated string
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/pos/archive/`,
        { id_list, is_archive: filters.showArchive ? 'NO' : 'YES' },
        { headers: { Authorization: `Bearer ${getSessionToken()}` } }
      );

      // Clear the selected rows and refresh the leads data
      setSelectedRows([]);
      fetchRenewals();
    } catch (error) {
      console.error('Error archiving leads:', error);
    }
  };

  const calculateDaysToExpire = (expiryDate) => {
    if (!expiryDate) return 'Invalid expiry date';

    const expiry = new Date(expiryDate);
    const today = new Date();

    expiry.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    const daysLeft = Math.floor((expiry - today) / (1000 * 60 * 60 * 24));

    if (daysLeft > 0) return `${daysLeft} Days to expire`;
    if (daysLeft === 0) return 'Expiring today';
    return `Expired`;
  };

  const columns = [
    {
      title: (
        <Checkbox
          checked={
            selectedRows.length > 0 && selectedRows.length === userData.length
          }
          indeterminate={
            selectedRows.length > 0 && selectedRows.length < userData.length
          }
          onChange={(e) => handleSelectAll(e.target.checked)}
        />
      ),
      align: 'center',
      render: (_, record) => (
        <Checkbox
          checked={selectedRows.includes(record.transaction_id)}
          onChange={(e) =>
            handleRowSelect(record.transaction_id, e.target.checked)
          }
        />
      ),
    },
    {
      title: (
        <div className="text-[12px] md:text-lg text-slate-600">Details</div>
      ),
      render: (_, record) => (
        <div className="flex">
          <div className="text-xl md:text-4xl h-9 md:h-14 mt-2 border-2 border-slate-300 rounded-lg pb-3 mr-3 shadow-[0_-2px_4px_rgba(0,0,0,0.3),0_2px_4px_rgba(0,0,0,0.3)] shadow-slate-300">
            {(() => {
              switch (record.transaction_id__insurance_sub_product_id__alias) {
                case 'privatecar':
                  return '🚗';
                case '2w':
                  return '🛵';
                case 'individual_health':
                case 'multi_individual_health':
                  return '🏥';
                case 'family_health':
                  return '👨‍👩‍👧‍👦';
                case 'individual_travel':
                  return '✈️';
                case 'family_travel':
                  return '✈️';
                case 'multi_trip_travel':
                  return '✈️';
                case 'student_travel':
                  return '✈️';
                case 'pcv':
                  return '🚌';
                case 'gcv':
                  return '🚚';
                case 'workman_componsation':
                  return '⚒️';
                case 'fire_burglar':
                  return '🔥';
                case 'marine':
                  return '⛴️';
                case 'home':
                  return '🏠';
                case 'term':
                  return '📜';
                case 'sme':
                  return '🏢';
                case 'gmc':
                  return '🏥';
                case 'gpa':
                  return '👤';
                case 'endowment':
                  return '💰';
                case 'ulip':
                  return '❤️';
                case 'life':
                  return '❤️';
                case 'miscellaneous':
                  return '🦺';
                default:
                  return '❓';
              }
            })()}
          </div>
          <div>
            <div className="text-slate-500 text-[10px] md:text-sm font-bold">
              {record.policy_holder_name || ''}
            </div>
            <div className="text-slate-500 text-[10px] md:text-sm font-bold">
              {record.transaction_id__transaction_user_input_motor_id__vehicle_registration_no?.toUpperCase()}
            </div>
            <div className="text-slate-500 text-[10px] md:text-sm font-bold">
              {record.policy_number}
            </div>
            <div className="text-slate-500 text-[10px] md:text-sm ">
              {record.transaction_id__transaction_user_input_motor_id__make} -{' '}
              {record.transaction_id__transaction_user_input_motor_id__model} -{' '}
              {record.transaction_id__transaction_user_input_motor_id__variant}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: (
        <div className="text-[12px] md:text-lg text-slate-600">
          Insurance Company
        </div>
      ),
      align: 'center',
      render: (_, record) => {
        return (
          <div className="flex items-center justify-center">
            {record.insurance_company_master_id__logo_url ? (
              <img
                src={record.insurance_company_master_id__logo_url}
                alt="Insurance Company Logo"
                className="h-4 md:h-8 w-auto" // Adjust height and width as needed
              />
            ) : (
              <span className="text-gray-500 text-[10px] md:text-sm">
                {record.insurance_company_master_id__name}
              </span>
            )}
          </div>
        );
      },
    },

    {
      title: (
        <div className="text-[12px] md:text-lg text-slate-600">
          Expiry Status
        </div>
      ),
      align: 'center',
      render: (_, record) => {
        const expiryStatus = calculateDaysToExpire(record.end_date);
        const isExpired =
          expiryStatus.includes('Expired') || expiryStatus.includes('today');
        return (
          <span
            className={`px-3 py-1 rounded-lg text-[10px] md:text-sm ${
              isExpired
                ? 'bg-red-100 text-red-600'
                : 'bg-green-100 text-green-600'
            }`}
          >
            {expiryStatus}
          </span>
        );
      },
    },
    {
      title: (
        <div className="text-[12px] md:text-lg text-slate-600">Actions</div>
      ),
      align: 'center',
      render: (_, record) => (
        <div className="w-full flex justify-center">
          <Button
            size="small"
            variant="default"
            className="rounded-lg text-[10px] md:text-sm"
            onClick={() => handleStartJourney(record)}
          >
            Continue <MdPlayArrow />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="w-full md:w-[70%] p-6 bg-gray-50 md:shadow-[0_-2px_4px_rgba(0,0,0,0.3),0_2px_4px_rgba(0,0,0,0.3)] md:shadow-slate-300 rounded-md m-auto mb-3">
      <div className="w-full text-start mb-4">
        <h2 className="text:xl md:text-2xl font-bold text-slate-500">
          Renewals
        </h2>
      </div>
      <div className="flex justify-between items-center mb-4 md:shadow-[0_-2px_4px_rgba(0,0,0,0.3),0_2px_4px_rgba(0,0,0,0.3)]  md:shadow-slate-300 rounded-lg md:py-6  md:p-3">
        <div className="flex gap-2 md:gap-4 items-center">
          <Select
            placeholder="Select Product"
            onChange={(value) => handleFilterChange('product', value)}
            className="w-40"
          >
            {products.map((product) => (
              <Select.Option key={product.value} value={product.value}>
                {product.label}
              </Select.Option>
            ))}
          </Select>
          <RangePicker
            placeholder={[datePlaceHolder.start_date, datePlaceHolder.end_date]}
            format="DD-MM-YYYY"
            onChange={(dates) =>
              handleFilterChange(
                'dateRange',
                dates ? dates.map((date) => date.format('DD-MM-YYYY')) : null
              )
            }
            className="placeholder:text-black"
          />
        </div>
        {selectedRows.length > 0 ? (
          <Button
            variant="default"
            onClick={handleDoArchive}
            className="rounded-lg text-[10px] md:text-sm ml-2"
          >
            {filters.showArchive ? 'Undo Archive' : 'Archive'}
          </Button>
        ) : (
          <Button
            variant="default"
            className="rounded-lg text-[10px] md:text-sm ml-2"
            onClick={() =>
              handleFilterChange('showArchive', !filters.showArchive)
            }
          >
            {filters.showArchive ? 'Show Renewals' : 'Show Archived'}
            {!filters.showArchive && <MdArchive className="ml-2 text-lg" />}
          </Button>
        )}
      </div>
      <Table
        columns={columns}
        dataSource={userData.map((item) => ({
          key: item.id,
          ...item,
        }))}
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total,
          onChange: (page) => setPagination({ ...pagination, current: page }),
          showSizeChanger: false,
        }}
        rowClassName={(record) =>
          selectedRows.includes(record.id) ? 'bg-blue-100' : ''
        }
      />
    </div>
  );
};

export default UpcomingRenewals;
